/* eslint relay/unused-fields: "off" */

import { FC, useMemo, useEffect } from 'react';
import { graphql, createRefetchContainer, RelayRefetchProp } from 'react-relay/legacy';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';

import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { useServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpRespContainer } from './PdpRespContainer/PdpRespContainer';
import { PdpSharedLayout } from './PdpSharedLayout';
import { PdpSharedAuctionContextProvider } from './PdpSharedAuctionContext/PdpSharedAuctionContext';
import { getPersonalizationTrackingData } from './tracking/personalizationTracking';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { DISABLE_TOP_YMAL_PDP } from '../constants/abTestsConstants';

import { PdpRespLayout_viewer$data } from './__generated__/PdpRespLayout_viewer.graphql';

import './styles/PdpRespLayout.scss';

export const PdpRespLayoutComponent: FC<{
    viewer: PdpRespLayout_viewer$data;
    relay: RelayRefetchProp;
}> = ({ viewer, relay }) => {
    const { isTablet } = useServerVarsContext();
    const { isPdpRespPreviewPage } = useSelector<$TSFixMe, { isPdpRespPreviewPage: boolean }>(
        state => ({
            isPdpRespPreviewPage: state?.pdpPreviewPage?.isPdpRespPreviewPage,
        })
    );

    useEffect(() => {
        trackAbTestV2Variant(DISABLE_TOP_YMAL_PDP);
    }, []);

    useEffect(() => {
        if (isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }, [isPdpRespPreviewPage]);

    const { similarLandingItems, personalizedItemsList } = viewer || {};

    const user = viewer.user || null;

    const item = viewer.itemRead?.[0] || null;
    const displayInfo = item?.displayInfo || null;

    const additionalTrackingData = useMemo(() => {
        return getPersonalizationTrackingData({ personalizedItems: personalizedItemsList });
    }, [personalizedItemsList]);

    if (!item) {
        return null;
    }

    return (
        <RecentlyViewedItemsProvider
            excludedItemId={item.serviceId}
            maxItems={isTablet ? 10 : 12}
            itemsPerPage={isTablet ? 5 : 6}
        >
            <PdpSharedAuctionContextProvider>
                <PdpSharedLayout
                    user={user}
                    viewer={viewer}
                    refreshPageOnTradeLogin={false}
                    additionalTrackingData={additionalTrackingData}
                    doRefetch={relay.refetch}
                    environment={relay.environment}
                >
                    {({ pdpHead, currency, onSkuChange, selectedSkuId }: $TSFixMe) => (
                        <>
                            <Helmet {...pdpHead.getPdpData()} />
                            <PdpRespContainer
                                viewer={viewer}
                                user={user}
                                item={item}
                                itemSearch={null}
                                currency={currency}
                                onSkuChange={onSkuChange}
                                displayInfo={displayInfo}
                                selectedSkuId={selectedSkuId}
                                similarLandingItems={similarLandingItems || null}
                            />
                        </>
                    )}
                </PdpSharedLayout>
            </PdpSharedAuctionContextProvider>
        </RecentlyViewedItemsProvider>
    );
};

//TODO: refactor to useRefechableFragment when PdpSharedLayout is converted to typescript
export const PdpRespLayout = createRefetchContainer(
    PdpRespLayoutComponent,
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer {
                ...PdpRespContainer_viewer
                ...PdpSharedLayout_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespContainer_user
                    ...PdpSharedLayout_user
                }
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        serviceId
                        ...PdpRespContainer_item
                        displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                            ...PdpRespContainer_displayInfo
                        }
                    }
                }
                ... @skip(if: $isTopYmalVariant) {
                    similarLandingItems: moreLikeThisItem(
                        first: 6
                        itemId: $itemId
                        isTrade: $isTrade
                        onlyAuctionItems: true
                        auctionModule: true
                    ) @include(if: $fetchPdp) {
                        ...PdpRespContainer_similarLandingItems
                    }
                }
                personalizedItemsList: personalizedItems(
                    userId: $userId
                    itemId: $itemId
                    pageSize: 10
                ) @include(if: $isClient) {
                    associationType
                    hasPersonalizedItem
                }
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $isClient: Boolean!
            $afterDisplay: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $isTrade: Boolean!
            $isMultiSku: Boolean!
            $fetchPdp: Boolean!
            $forcedCurrency: Currency
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $priceBookName: String = ""
            $isInteractivePdpChat: Boolean = false
            $timezoneOffset: Int
            $fetchAuction: Boolean = false
            $itemQuantity: Int
            $isTopYmalVariant: Boolean = false
            $fdfQuoteABTestVariant: String
        ) {
            viewer {
                ...PdpRespLayout_viewer
            }
        }
    `
);
